import { m } from 'framer-motion';
import { Theme, SxProps } from '@mui/material/styles';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { ForbiddenIllustration } from '@/assets/illustrations';
import { varBounce, MotionContainer } from '@/components/utils/animate';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../hooks';

type PermissionGuardProp = {
  hasContent?: boolean;
  module: string;
  submodule: string;
  access: string;
  ownerID?: string;
  children: React.ReactNode;
  sx?: SxProps<Theme>;
};

export default function PermissionGuard({
  hasContent,
  children,
  sx,
  module,
  submodule,
  access,
  ownerID,
}: PermissionGuardProp) {
  const { user } = useAuthContext();
  const { t } = useTranslation();

  // Memoize permission check to avoid unnecessary recalculations
  const hasPermission = useMemo(
    () => user?.checkPermission(module, submodule, access, ownerID),
    [user, module, submodule, access, ownerID]
  );

  // Memoize container styles
  const containerStyles = useMemo((): SxProps<Theme> => ({ textAlign: 'center', ...sx }), [sx]);

  // Memoize title styles
  const titleStyles = useMemo((): SxProps<Theme> => ({ mb: 2 }), []);

  // Memoize description styles
  const descriptionStyles = useMemo((): SxProps<Theme> => ({ color: 'text.secondary' }), []);

  // Memoize illustration styles
  const illustrationStyles = useMemo(
    (): SxProps<Theme> => ({
      height: 260,
      my: { xs: 5, sm: 10 },
    }),
    []
  );

  // Memoize bounce animation variants
  const bounceVariants = useMemo(() => varBounce().in, []);

  // Memoize permission denied content
  const permissionDeniedContent = useMemo(() => {
    if (!hasContent) return null;

    return (
      <Container component={MotionContainer} sx={containerStyles}>
        <m.div variants={bounceVariants}>
          <Typography variant="h3" sx={titleStyles}>
            {t('Permission Denied')}
          </Typography>
        </m.div>

        <m.div variants={bounceVariants}>
          <Typography sx={descriptionStyles}>
            {t('You do not have permission to access this page')}
          </Typography>
        </m.div>

        <m.div variants={bounceVariants}>
          <ForbiddenIllustration sx={illustrationStyles} />
        </m.div>
      </Container>
    );
  }, [
    hasContent,
    containerStyles,
    bounceVariants,
    titleStyles,
    descriptionStyles,
    illustrationStyles,
    t,
  ]);

  if (!hasPermission) {
    return permissionDeniedContent;
  }

  return <>{children}</>;
}
